import { CalculatorOutlined, MinusCircleOutlined, MinusOutlined, SearchOutlined, CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined, PlayCircleOutlined, UnorderedListOutlined, LinkOutlined } from "@ant-design/icons";
import { Button, Card, Col, DatePicker, Empty, message, Row, Select, Spin, Statistic, Switch, Typography } from "antd";
import dayjs from 'dayjs';
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import 'dayjs/locale/pt-br';
import 'dayjs/locale/en';
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import moment from "moment";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import { MAP_TILE_URL, MAP_ATTRIBUTION } from "../../config/mapConfig";
import MarkerClusterGroup from "react-leaflet-cluster";
import { AutoSizer, CellMeasurer, CellMeasurerCache, List as RVList } from "react-virtualized";
import { SelectSearch } from "../../components/SelectSearch";
import { TreeSelectLocationUser } from "../../components/TreeSelectLocation";
import { UserPicker } from "../../components/UserPicker";
import { useAuth } from "../../hooks/auth";
import Api from "../../services/api";
import { Container } from "../PowerBI/styles";
import PeriodDot from "./components/PeriodDot";
import { formatDateRange as formatDateRangeUtil, isValidArray } from "./utils/comparisonUtils";
import { periodWithDotContainerStyle } from "./styles/comparisonStyles";

// Adiciona estilos CSS para animaÃ§Ãµes e mapa azul
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = `
    @keyframes fadeInRow {
        from {
            opacity: 0;
            transform: translateY(5px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
    @keyframes fadeIn {
        from {
            opacity: 0;
            transform: translateX(-10px);
        }
        to {
            opacity: 1;
            transform: translateX(0);
        }
    }
`;
if (!document.head.querySelector('style[data-dashboard-animations]')) {
  styleSheet.setAttribute('data-dashboard-animations', 'true');
  document.head.appendChild(styleSheet);
}
import ColoredBarChartWithDetails from "./charts/barchart";
import PieChartWithDetails from "./charts/piechart";
import DonutChartWithDetails from "./charts/pizzachart";
import PieChartComparison from "./charts/pieChartComparison";
import DonutChartComparison from "./charts/donutChartComparison";
import BarChartComparison from "./charts/barChartComparison";
import LocationTree from "./locationTreeCustom";
import LocationTreeComparison from "./locationTreeComparison";
import AnswersByDateChart from "./charts/answersByDateChart";

dayjs.extend(utc);
dayjs.extend(timezone);

// Configura os Ã­cones do Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png",
});

// FunÃ§Ã£o para criar Ã­cone de cluster personalizado com cores azuis
const createClusterIcon = (cluster) => {
  const count = cluster.getChildCount();

  // Define cores azuis progressivamente mais escuras
  let color;
  let size;
  if (count < 10) {
    color = '#91d5ff'; // Azul muito claro
    size = 40;
  } else if (count < 50) {
    color = '#69c0ff'; // Azul claro
    size = 45;
  } else if (count < 100) {
    color = '#4085c6'; // Azul padrÃ£o do projeto
    size = 50;
  } else if (count < 500) {
    color = '#32639e'; // Azul mÃ©dio
    size = 55;
  } else {
    color = '#1669b6'; // Azul escuro
    size = 60;
  }

  return L.divIcon({
    html: `<div style="
            background-color: ${color};
            width: ${size}px;
            height: ${size}px;
            border-radius: 50%;
            border: 3px solid #fff;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #fff;
            font-weight: 700;
            font-size: ${count < 100 ? '14px' : '16px'};
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        ">${count}</div>`,
    className: 'custom-cluster-icon',
    iconSize: L.point(size, size)
  });
};

// FunÃ§Ã£o para renderizar mapa a partir de valor de geolocalizaÃ§Ã£o
// Formato esperado: "lat,lng;lat,lng;..."
const renderGeolocationMap = (value, t) => {
  if (!value || (Array.isArray(value) && value.length === 0)) {
    return <Empty description={t("Dashboard.NoGeolocationAvailable")} />;
  }

  let markers = [];

  // FunÃ§Ã£o auxiliar para tentar converter uma string de coordenadas
  const parseCoordinate = (coordinateString) => {
    try {
      // Se nÃ£o contÃ©m vÃ­rgula mas contÃ©m ponto-e-vÃ­rgula, interpretamos como "lat;lng"
      if (coordinateString.indexOf(",") === -1 && coordinateString.indexOf(";") !== -1) {
        const coords = coordinateString.split(";").map((p) => p.trim());
        if (coords.length === 2) {
          const lat = parseFloat(coords[0]);
          const lng = parseFloat(coords[1]);
          if (!isNaN(lat) && !isNaN(lng)) {
            return { lat, lng };
          }
        }
      } else {
        // Caso contrÃ¡rio, assume "lat,lng"
        const coords = coordinateString.split(",").map((p) => p.trim());
        if (coords.length === 2) {
          const lat = parseFloat(coords[0]);
          const lng = parseFloat(coords[1]);
          if (!isNaN(lat) && !isNaN(lng)) {
            return { lat, lng };
          }
        }
      }
    } catch (error) {
      console.error("Erro ao fazer parse da coordenada:", coordinateString, error);
    }
    return null;
  };

  // Se for um array, itera sobre cada item
  if (Array.isArray(value)) {
    value.forEach((item) => {
      if (!item) return; // ignora valores nulos ou falsos
      const coordinate = parseCoordinate(item);
      if (coordinate) {
        markers.push(coordinate);
      }
    });
  } else if (typeof value === "string") {
    try {
      // Se a string inteira Ã© um Ãºnico par "lat;lng"
      if (value.indexOf(",") === -1 && value.indexOf(";") !== -1) {
        const coordinate = parseCoordinate(value);
        if (coordinate) {
          markers.push(coordinate);
        }
      } else {
        // Separa por ponto-e-vÃ­rgula para obter cada par "lat,lng"
        const parts = value.split(";").map((p) => p.trim()).filter((p) => p);
        parts.forEach((part) => {
          const coordinate = parseCoordinate(part);
          if (coordinate) {
            markers.push(coordinate);
          }
        });
      }
    } catch (error) {
      console.error("Erro ao processar a string de geolocalizaÃ§Ã£o:", value, error);
    }
  }

  if (markers.length === 0) {
    return <Empty description={t("Dashboard.NoGeolocationAvailable")} />;
  }

  // Define o centro do mapa com base no primeiro marcador
  const center = [markers[0].lat, markers[0].lng];

  return (
    <MapContainer
      center={center}
      zoom={markers.length > 1 ? 5 : 10}
      style={{ height: 250, width: "100%" }}
      className="blue-map-container"
    >
      <TileLayer
        url={MAP_TILE_URL}
        attribution={MAP_ATTRIBUTION}
      />
      <MarkerClusterGroup
        iconCreateFunction={createClusterIcon}
      >
        {markers.map((marker, idx) => (
          <Marker key={idx} position={[marker.lat, marker.lng]}>
            <Popup>
              <>
                <Typography.Text><strong>{t ? t("Dashboard.Latitude") : "Lat"}:</strong> {marker.lat}</Typography.Text>
                <br />
                <Typography.Text><strong>{t ? t("Dashboard.Longitude") : "Lng"}:</strong> {marker.lng}</Typography.Text>
              </>
            </Popup>
          </Marker>
        ))}
      </MarkerClusterGroup>
      <FullscreenControl t={t} />
    </MapContainer>
  );
};

const FullscreenControl = ({ t }) => {
  const map = useMap();

  useEffect(() => {
    const handleFullscreen = () => {
      if (!document.fullscreenElement) {
        map.getContainer().requestFullscreen();
      } else {
        document.exitFullscreen();
      }
    };

    const fullscreenButton = L.control({ position: "topright" });

    fullscreenButton.onAdd = () => {
      const btn = L.DomUtil.create("button", "leaflet-bar leaflet-control leaflet-control-custom");
      btn.innerHTML = "â›¶";
      btn.style.width = "30px";
      btn.style.height = "30px";
      btn.style.cursor = "pointer";
      btn.title = t("Dashboard.Fullscreen");
      btn.onclick = handleFullscreen;
      return btn;
    };

    fullscreenButton.addTo(map);

    return () => {
      fullscreenButton.remove();
    };
  }, [map, t]);

  return null;
};

// Componente de Cards de Status das Tarefas
const TaskStatusCards = ({ tasksData, taskStatusCounts: taskStatusCountsProp, taskStatusCounts2: taskStatusCounts2Prop, isComparison = false, t, loading = false }) => {
  // Usar contagens do backend (taskStatusCounts) quando disponÃ­vel; senÃ£o calcular a partir de tasksData
  const statusCounts = useMemo(() => {
    if (taskStatusCountsProp) {
      const approval = taskStatusCountsProp.Approval ?? taskStatusCountsProp.approval ?? 0;
      const pending = taskStatusCountsProp.Pending ?? taskStatusCountsProp.pending ?? 0;
      const late = taskStatusCountsProp.Late ?? taskStatusCountsProp.late ?? 0;
      const open = taskStatusCountsProp.Open ?? taskStatusCountsProp.open ?? 0;
      const done = taskStatusCountsProp.Done ?? taskStatusCountsProp.done ?? 0;
      const fallbackTotal = approval + pending + late + open + done;
      const backendTotal = taskStatusCountsProp.Total ?? taskStatusCountsProp.total;
      return {
        approval,
        pending,
        late,
        open,
        done,
        total: backendTotal != null ? backendTotal : fallbackTotal
      };
    }
    if (!tasksData) {
      return {
        approval: 0,
        pending: 0,
        late: 0,
        open: 0,
        done: 0,
        total: 0
      };
    }

    const { open = [], late = [], done = [], approval = [] } = tasksData;

    // Tarefas pendentes: status Pending do array 'open' (tarefas nÃ£o atrasadas)
    const pendingTasks = open.filter(task => {
      const status = task.status || task.Status;
      return status === 'Pending';
    });

    // Tarefas em andamento: status Open do array 'open'
    const openTasks = open.filter(task => {
      const status = task.status || task.Status;
      return status === 'Open';
    });

    // Total: soma de todos os arrays
    const total = open.length + late.length + done.length + approval.length;

    return {
      approval: approval.length,
      pending: pendingTasks.length,
      late: late.length,
      open: openTasks.length,
      done: done.length,
      total: total
    };
  }, [tasksData, taskStatusCountsProp]);

  // Contagens do perÃ­odo 2 (comparaÃ§Ã£o): mesmo formato camelCase que statusCounts
  const statusCounts2 = useMemo(() => {
    if (!taskStatusCounts2Prop) {
      return { approval: 0, pending: 0, late: 0, open: 0, done: 0, total: 0 };
    }
    const approval = taskStatusCounts2Prop.Approval ?? taskStatusCounts2Prop.approval ?? 0;
    const pending = taskStatusCounts2Prop.Pending ?? taskStatusCounts2Prop.pending ?? 0;
    const late = taskStatusCounts2Prop.Late ?? taskStatusCounts2Prop.late ?? 0;
    const open = taskStatusCounts2Prop.Open ?? taskStatusCounts2Prop.open ?? 0;
    const done = taskStatusCounts2Prop.Done ?? taskStatusCounts2Prop.done ?? 0;
    const fallbackTotal = approval + pending + late + open + done;
    const backendTotal = taskStatusCounts2Prop.Total ?? taskStatusCounts2Prop.total;
    return {
      approval,
      pending,
      late,
      open,
      done,
      total: backendTotal != null ? backendTotal : fallbackTotal
    };
  }, [taskStatusCounts2Prop]);

  const quickActions = [
    {
      key: "approval",
      title: "Em aprovaÃ§Ã£o",
      icon: ClockCircleOutlined,
      color: "#60a5fa",
      count: statusCounts.approval,
      count2: statusCounts2.approval,
    },
    {
      key: "pending",
      title: "Tarefas pendentes",
      icon: ClockCircleOutlined,
      color: "#60a5fa",
      count: statusCounts.pending,
      count2: statusCounts2.pending,
    },
    {
      key: "late",
    title: "Tarefas atrasadas",
      icon: ExclamationCircleOutlined,
      color: "#f87171",
      count: statusCounts.late,
      count2: statusCounts2.late,
    },
    {
      key: "open",
      title: "Tarefas em Rascunho",
      icon: PlayCircleOutlined,
      color: "#60a5fa",
      count: statusCounts.open,
      count2: statusCounts2.open,
    },
    {
      key: "done",
      title: "Tarefas Finalizadas",
      icon: CheckCircleOutlined,
      color: "#34d399",
      count: statusCounts.done,
      count2: statusCounts2.done,
    },
    {
      key: "total",
      title: t("Dashboard.Tooltip.Total"),
      icon: UnorderedListOutlined,
      color: "#4085c6",
      count: statusCounts.total,
      count2: statusCounts2.total,
    },
  ];

  const { Text, Title } = Typography;

  return (
    <Row gutter={[8, 8]} wrap style={{ marginBottom: 24 }}>
      {quickActions.map((action) => {
        const Icon = action.icon;
        return (
          <Col
            xs={24}
            sm={12}
            md={8}
            lg={6}
            xl={4}
            key={action.key}
          >
            <Card
              style={{
                height: 120,
                display: "flex",
                alignItems: "center",
              }}
              bodyStyle={{
                padding: 16,
                height: "100%",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Row align="center" style={{ width: "100%" }}>
                <Icon
                  style={{
                    fontSize: 28,
                    color: action.color,
                    marginRight: 12,
                    flexShrink: 0,
                  }}
                />
                <Col flex={1} style={{ minWidth: 0 }}>
                  <Text
                    style={{
                      marginBottom: 4,
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      fontSize: 13,
                      lineHeight: '1.4',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {action.title}
                  </Text>
                  {loading ? (
                    <Spin size="small" />
                  ) : isComparison ? (
                    <Title
                      level={4}
                      style={{
                        marginTop: 0,
                        marginBottom: 0,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0,
                      }}
                    >
                      <span>{action.count}</span>
                      <span
                        style={{
                          marginLeft: 8,
                          marginRight: 8,
                          borderLeft: '1px solid #d9d9d9',
                          alignSelf: 'stretch',
                        }}
                      />
                      <span>{action.count2}</span>
                    </Title>
                  ) : (
                    <Title
                      level={4}
                      style={{
                        marginTop: 0,
                        marginBottom: 0,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {action.count}
                    </Title>
                  )}
                </Col>
              </Row>
            </Card>
          </Col>
        );
      })}
    </Row>
  );
};

const NewPowerBI = ({ formId = null, userId = null }) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  // Configure dayjs locale based on user language
  const currentLanguage = user?.languageId || 'pt-BR';
  const dayjsLocale = currentLanguage === 'en-US' ? 'en' : 'pt-br';

  // Update dayjs locale when language changes
  useEffect(() => {
    dayjs.locale(dayjsLocale);
  }, [dayjsLocale]);

  const [loading, setLoading] = useState(true);
  const [cols, setCols] = useState(3);
  const filterFieldStyle = { width: 230, minWidth: 230 };

  // FunÃ§Ã£o helper para calcular breakpoints responsivos baseado no nÃºmero de colunas
  // Limita o nÃºmero de colunas para garantir que os grÃ¡ficos tenham tamanho mÃ­nimo viÃ¡vel
  const getResponsiveColProps = (desiredCols) => {
    // Define breakpoints responsivos com limitaÃ§Ã£o de colunas
    // xs: < 576px (mobile)
    // sm: >= 576px (tablet pequeno)
    // md: >= 768px (tablet)
    // lg: >= 992px (desktop pequeno)
    // xl: >= 1200px (desktop)
    // xxl: >= 1600px (desktop grande)

    // Calcula o span baseado no nÃºmero de colunas desejado
    // Permite qualquer nÃºmero de colunas, os grÃ¡ficos se adaptam ao tamanho
    const baseSpan = Math.floor(24 / desiredCols);

    return {
      xs: 24,      // Mobile: sempre 1 coluna (100% da largura)
      sm: 24,      // Tablet pequeno: 1 coluna
      md: desiredCols >= 4 ? 12 : desiredCols >= 3 ? 12 : 24,  // Tablet: 2 colunas se 3+, senÃ£o 1
      lg: desiredCols >= 4 ? 6 : desiredCols >= 3 ? 8 : 12,    // Desktop mÃ©dio: 4 colunas se 4, 3 se 3, 2 se 2
      xl: baseSpan, // Desktop/Full HD: usa o span calculado (6 para 4 colunas, 8 para 3, etc)
      xxl: baseSpan // Desktop extra grande: usa o span calculado
    };
  };
  const [forms, setForms] = useState([]);
  const [formsFilhos, setFormsFilhos] = useState([]);
  const [form, setForm] = useState();
  const [formFilho, setFormFilho] = useState();
  const [formsOptions, setFormsOptions] = useState([]);
  const [formsFilhosOptions, setFormsFilhosOptions] = useState([]);
  const [clear, setClear] = useState(false);
  const [tasksByLocation, setTasksByLocation] = useState([]);
  const [lastExecution, setLastExecution] = useState(null);

  const [taskIdsList, setTaskIdsList] = useState([]);
  const [filterApplied, setFilterApplied] = useState(false);

  // Mover antes dos useEffects
  const [filterAnswersRequest, setFilterAnswersRequest] = useState({
    userId: undefined,
    locationId: undefined,
    dateRange: [dayjs().startOf("month"), dayjs().endOf("month")],
    startDate: dayjs().startOf("month"),
    endDate: dayjs().endOf("month"),
    locationIncludeChildren: true
  });

  const [comparisonFilterEnabled, setComparisonFilterEnabled] = useState(false);
  const [comparisonApplied, setComparisonApplied] = useState(false);
  const [comparisonVisualMode, setComparisonVisualMode] = useState(false);
  const [isLocationTreeOpen, setIsLocationTreeOpen] = useState(false);

  const filterAnswersRequestRef = useRef(filterAnswersRequest);
  const selectedFiltersRef = useRef({});
  filterAnswersRequestRef.current = filterAnswersRequest;

  const loadedFormIdRef = useRef(null);

  useEffect(() => {
    Api.get(`PowerBI/GetForms`)
      .then(res => {
        const opts = res.data.response.map(i => ({ label: i.name, value: i.id }));
        setForms(res.data.response);
        setFormsOptions(opts);
        setLoading(false);
      })
      .catch(() => setLoading(false));

    Api.post(`PowerBI/LastExecution`)
      .then(res => {
        const dt = dayjs.utc(res.data.response).local().format("DD/MM/YYYY HH:mm");
        setLastExecution(dt);
      })
      .catch(() => { });

    // Se showOnlyMyTasks estÃ¡ ativo, define o userId automaticamente
    if (user?.showOnlyMyTasks && user?.id) {
      setFilterAnswersRequest(prev => {
        const next = { ...prev, userId: [user.id] };
        filterAnswersRequestRef.current = next;
        return next;
      });
    }
  }, [user]);

  useEffect(() => {
    if (formId && forms.length > 0 && !form && loadedFormIdRef.current != formId) {
      const selected = forms.find(f => f.id == formId);
      if (selected) {
        setForm(formId);
        setFormsFilhos(selected.childs || []);
        setFormsFilhosOptions(
          (selected.childs || []).map(c => ({ label: `(v${c.version}) ${c.name}`, value: c.id }))
        );

        setFilterAnswersRequest(prev => {
          const next = {
            ...prev,
            userId: userId ? [userId] : null,
            dateRange: undefined,
            startDate: undefined,
            endDate: undefined,
          };
          filterAnswersRequestRef.current = next;
          return next;
        });

        loadedFormIdRef.current = formId;
      }
    }
  }, [formId, forms, form, userId]);

  useEffect(() => {
    if (form) {
      setFilterApplied(true);
    }
  }, [form]);

  // Agrupa tarefas filtradas por status
  const filteredTasksData = useMemo(() => {
    const hasDateFilter = (filterAnswersRequest?.dateRange2 &&
      filterAnswersRequest?.dateRange2[0] &&
      filterAnswersRequest?.dateRange2[1]) ||
      (filterAnswersRequest?.dateRange &&
        filterAnswersRequest?.dateRange[0] &&
        filterAnswersRequest?.dateRange[1]);

    if (!hasDateFilter || !taskIdsList || taskIdsList.length === 0) {
      return null;
    }

    const today = dayjs().startOf('day');

    const open = [];
    const late = [];
    const done = [];
    const approval = [];

    taskIdsList.forEach((task) => {
      const status = task.Status || task.status || '';
      const scheduleDate = task.ScheduleDate ? dayjs(task.ScheduleDate).startOf('day') : null;

      if (status === 'Done') {
        done.push(task);
      } else if (status === 'Approval') {
        approval.push(task);
      } else if (status === 'Open' || status === 'Pending') {
        // Tarefas atrasadas: ScheduleDate anterior a hoje
        if (scheduleDate && scheduleDate.isBefore(today)) {
          late.push(task);
        } else {
          open.push(task);
        }
      } else if (status === 'Late') {
        late.push(task);
      }
    });

    return {
      open,
      late,
      done,
      approval
    };
  }, [taskIdsList, filterAnswersRequest?.dateRange2, filterAnswersRequest?.dateRange]);

  const renderAnswersUserDropdown = () => {
    return (
      <UserPicker
        placeholder={t("AssignedTo")}
        onChange={handleAnswersSearchUser}
        value={filterAnswersRequest.userId}
        disabled={!!!form || loading}
        count={0}
        style={{ width: "100%" }}
      />
    );
  };

  const renderAnswersLocationDropdown = () => {
    return (
      <TreeSelectLocationUser
        placeholder={t("Location")}
        includeDirectParents={false}
        disabled={!!!form || loading}
        onChange={(p) => handleAnswersSearchLocation(p, "combo")}
        open={isLocationTreeOpen}
        onDropdownVisibleChange={(open) => setIsLocationTreeOpen(open)}
        onOpenChange={(open) => setIsLocationTreeOpen(open)}
        value={filterAnswersRequest.locationId}
        style={{ width: "100%" }}
      />
    );
  };

  const renderAnswersLocationBelowDropdown = () => {
    if (filterAnswersRequest?.locationId != undefined && filterAnswersRequest?.locationId != "")
      return (
        <><Switch disabled={!!!form || loading} style={{ marginTop: 10 }} value={filterAnswersRequest?.locationIncludeChildren} onChange={(p) => handleAnswersSearchLocation(p, "switchbelow")} /> {t('History.LocationBelow')}</>
      );
    else return <></>
  };

  const onChange = async (value) => {
    const fx = forms.find((i) => i.id === value);
    if (fx) {
      setForm(value);
      setFormFilho(undefined);
      setFilterAnswersRequest((prev) => {
        const next = {
          ...prev,

        };
        filterAnswersRequestRef.current = next;
        return next;
      });
      const opts = [];
      fx.childs.forEach((i) =>
        opts.push({
          label: "(v" + i.version + ") " + i.name,
          value: i.id,
        })
      );
      setFormsFilhos(fx.childs);
      setFormsFilhosOptions(opts);
    }
  };

  const onChangeFilho = async (value) => {
    if (!value) return;
    setFormFilho(formsFilhos.find((i) => i.id === value));
  };

  const handleAnswersSearchUser = (value) => {
    // Se showOnlyMyTasks estÃ¡ ativo, nÃ£o permite alterar o userId
    if (user?.showOnlyMyTasks) {
      return;
    }
    setFilterAnswersRequest((prev) => {
      const next = { ...prev, userId: value };
      filterAnswersRequestRef.current = next;
      return next;
    });
  };

  const handleAnswersSearchLocation = (value, field = "combo") => {
    if (field === "combo") {
      setIsLocationTreeOpen(false);
    }
    setFilterAnswersRequest((prev) => {
      const next = {
        ...prev,
        locationId: field === "combo" ? value : prev.locationId,
        locationIncludeChildren: field === "switchbelow" ? value : prev.locationIncludeChildren,
      };
      filterAnswersRequestRef.current = next;
      return next;
    });
  };

  const handleOthersClick = useCallback(() => {
    setIsLocationTreeOpen(true);
  }, []);

  const handleDateRangeChange = (dates) => {
    if (dates) {
      const [start, end] = dates.map((date) => date.toISOString());
      setFilterAnswersRequest((prev) => {
        const next = { ...prev, dateRange: dates, startDate: start, endDate: end };
        filterAnswersRequestRef.current = next;
        return next;
      });
    } else {
      setFilterAnswersRequest((prev) => {
        const next = { ...prev, dateRange: undefined, startDate: undefined, endDate: undefined };
        filterAnswersRequestRef.current = next;
        return next;
      });
    }
  };

  const handleDateRangeChange2 = (dates) => {
    if (dates) {
      const [start, end] = dates.map((date) => date.toISOString());
      setFilterAnswersRequest((prev) => {
        const next = { ...prev, dateRange2: dates, startDate2: start, endDate2: end };
        filterAnswersRequestRef.current = next;
        return next;
      });
    } else {
      setFilterAnswersRequest((prev) => {
        const next = { ...prev, dateRange2: undefined, startDate2: undefined, endDate2: undefined };
        filterAnswersRequestRef.current = next;
        return next;
      });
    }
  };

  // Ao ativar "Habilitar comparaÃ§Ã£o", inicializar P2 com as datas de P1 para garantir segunda requisiÃ§Ã£o com datas vÃ¡lidas
  const handleComparisonToggle = (checked) => {
    setComparisonFilterEnabled(checked);
    if (checked) {
      setFilterAnswersRequest((prev) => {
        const dates = prev.dateRange && prev.dateRange[0] && prev.dateRange[1]
          ? prev.dateRange
          : (prev.startDate != null && prev.endDate != null ? [dayjs(prev.startDate), dayjs(prev.endDate)] : null);
        if (dates && dates.length >= 2) {
          const [start, end] = dates.map((d) => (typeof d.toISOString === 'function' ? d.toISOString() : String(d)));
          const next = { ...prev, dateRange2: dates, startDate2: start, endDate2: end };
          filterAnswersRequestRef.current = next;
          return next;
        }
        return prev;
      });
    }
  };

  const [questions, setQuestions] = useState(null);
  const [formDataMax, setFormDataMax] = useState(null);
  const [formAnswers, setFormAnswers] = useState(null);
  const [lastLoadedForm, setLastLoadedForm] = useState(null);
  const [lastLoadedFormFilho, setLastLoadedFormFilho] = useState(null);

  // Contagens por status vindas do backend (BI - todos os status, nÃ£o sÃ³ Done)
  const [taskStatusCounts, setTaskStatusCounts] = useState(null);

  // Estados para dados do perÃ­odo 2 (comparaÃ§Ã£o)
  const [formAnswers2, setFormAnswers2] = useState(null);
  const [taskStatusCounts2, setTaskStatusCounts2] = useState(null);
  const [tasksByLocation2, setTasksByLocation2] = useState([]);
  const [taskIdsList2, setTaskIdsList2] = useState([]);

  // Componente wrapper para o card do grÃ¡fico com botÃ£o no header
  const ChartCardWrapper = ({ cols, getResponsiveColProps, title, loading, visualSubTitle, renderChart }) => {
    const [headerButton, setHeaderButton] = useState(null);

    return (
      <Col {...getResponsiveColProps(cols)} style={{ display: 'flex', flexDirection: 'column' }}>
        <Card
          style={{
            height: '100%',
            marginBottom: 0,
            display: 'flex',
            flexDirection: 'column'
          }}
          bodyStyle={{
            padding: 16,
            flex: 1,
            minHeight: 300,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}
          title={
            <Row justify="space-between" align="flex-start" style={{ width: '100%', gap: '8px', minWidth: 0 }}>
              <Col flex={1} style={{ minWidth: 0 }}>
                <Typography.Text style={{ fontSize: 15, fontWeight: 600, color: '#262626', wordBreak: 'break-word', overflowWrap: 'break-word', lineHeight: '1.4', whiteSpace: 'normal' }}>{title}</Typography.Text>
              </Col>
              {headerButton && (
                <Col style={{ flexShrink: 0, marginTop: '2px' }}>
                  {headerButton}
                </Col>
              )}
            </Row>
          }
        >
          {loading ? (
            <Row justify="center" align="middle" style={{ padding: '60px 0' }}>
              <Spin size="large" />
            </Row>
          ) : (
            <>
              {renderChart(setHeaderButton)}
              {visualSubTitle && (
                <Typography.Text type="secondary" style={{ marginTop: 16, fontSize: 13, fontStyle: 'italic', display: 'block' }}>
                  {visualSubTitle}
                </Typography.Text>
              )}
            </>
          )}
        </Card>
      </Col>
    );
  };

  const renderChart = (x2, data, categories, onRenderHeaderButton, data2 = null, categories2 = null, renderMode = 'both') => {
    const fieldId = x2.previousVersionId ?? x2.id;
    const activeFilters = selectedFilters[fieldId] || [];
    const isComparison = isComparisonVisualMode() && data2 && categories2;

    switch (x2.chartType) {
      case "barChart":
        if (isComparison) {
          return (
            <BarChartComparison
              data={data}
              data2={data2}
              categories={categories}
              categories2={categories2}
              disabled={loading}
              activeFilters={activeFilters}
              columnsCount={cols}
              onRenderHeaderButton={onRenderHeaderButton}
              renderMode={renderMode}
              onClick={(valor) =>
                handleFilterSelect(fieldId, valor)
              }
              onOthersClick={handleOthersClick}
            />
          );
        }
        return (
          <ColoredBarChartWithDetails
            data={data}
            categories={categories}
            disabled={loading}
            activeFilters={activeFilters}
            columnsCount={cols}
            onRenderHeaderButton={onRenderHeaderButton}
            onClick={(valor) =>
              handleFilterSelect(fieldId, valor)
            }
          />
        );
      case "pieChart":
        if (isComparison) {
          return (
            <PieChartComparison
              data={data}
              data2={data2}
              categories={categories}
              categories2={categories2}
              disabled={loading}
              activeFilters={activeFilters}
              columnsCount={cols}
              onRenderHeaderButton={onRenderHeaderButton}
              renderMode={renderMode}
              onClick={(valor) =>
                handleFilterSelect(fieldId, valor)
              }
              onOthersClick={handleOthersClick}
            />
          );
        }
        return (
          <PieChartWithDetails
            data={data}
            categories={categories}
            disabled={loading}
            activeFilters={activeFilters}
            columnsCount={cols}
            onRenderHeaderButton={onRenderHeaderButton}
            onClick={(valor) =>
              handleFilterSelect(x2.previousVersionId ?? x2.id, valor)
            }
          />
        );
      case "donutChart":
        if (isComparison) {
          return (
            <DonutChartComparison
              data={data}
              data2={data2}
              categories={categories}
              categories2={categories2}
              disabled={loading}
              activeFilters={activeFilters}
              columnsCount={cols}
              onRenderHeaderButton={onRenderHeaderButton}
              renderMode={renderMode}
              onClick={(valor) =>
                handleFilterSelect(x2.previousVersionId ?? x2.id, valor)
              }
              onOthersClick={handleOthersClick}
            />
          );
        }
        return (
          <DonutChartWithDetails
            data={data}
            categories={categories}
            disabled={loading}
            activeFilters={activeFilters}
            columnsCount={cols}
            onRenderHeaderButton={onRenderHeaderButton}
            onClick={(valor) =>
              handleFilterSelect(x2.previousVersionId ?? x2.id, valor)
            }
          />
        );
      default:
        return <p>{t("Dashboard.UnsupportedChartType")}</p>;
    }
  };

  const renderAgr = (x2, data, categories) => {
    const numericCategories = categories.map((cat) => parseFloat(cat) || 0);
    const totalValue = numericCategories.reduce(
      (sum, value, index) => sum + value * (data[index] || 0),
      0
    );
    const totalReports = data.reduce((sum, count) => sum + count, 0);

    // Verifica se Ã© moeda
    const isCurrency = x2.questionTypeCode === 'currency';
    const currencyCode = x2.currency || 'BRL';

    // Calcula ambas as mÃ©tricas
    const sumValue = totalValue;
    const avgValue = totalReports > 0 ? totalValue / totalReports : 0;

    // Formata o valor com arredondamento inteligente
    const formatValue = (value) => {
      if (value === 0) return isCurrency ? '0' : '0';

      if (isCurrency) {
        // FormataÃ§Ã£o de moeda
        try {
          return new Intl.NumberFormat(currentLanguage === 'en-US' ? 'en-US' : 'pt-BR', {
            style: 'currency',
            currency: currencyCode,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(value);
        } catch (e) {
          // Fallback se moeda nÃ£o for suportada
          const symbol = currencyCode === 'USD' ? '$' : currencyCode === 'EUR' ? 'â‚¬' : 'R$';
          return `${symbol} ${value.toFixed(2)}`;
        }
      } else {
        // FormataÃ§Ã£o numÃ©rica com K, M, B
        const absValue = Math.abs(value);
        if (absValue >= 1000000000) {
          return (value / 1000000000).toFixed(2) + 'B';
        } else if (absValue >= 1000000) {
          return (value / 1000000).toFixed(2) + 'M';
        } else if (absValue >= 1000) {
          return (value / 1000).toFixed(2) + 'K';
        } else if (absValue >= 1) {
          return value.toFixed(2);
        } else {
          return value.toFixed(4);
        }
      }
    };

    // Sempre exibe ambas as mÃ©tricas quando agregationType estÃ¡ definido
    if (x2.agregationType) {
      return (
        <Row style={{ width: '100%' }}>
          <Col span={24}>
            <Typography.Text style={{ textAlign: 'center', marginBottom: 50, fontSize: 16, color: '#595959', fontWeight: 500, display: 'block' }}>
              {t("Dashboard.Answers")} : {totalReports}
            </Typography.Text>
          </Col>
          <Col span={24}>
            <Row gutter={[16, 16]}>
              <Col span={12} style={{ textAlign: 'center' }}>
                <Statistic
                  title={t("Dashboard.Sum")}
                  value={formatValue(sumValue)}
                  precision={2}
                  valueStyle={{ color: "#4085c6", fontSize: 42 }}
                  prefix={<CalculatorOutlined />}
                />
              </Col>
              <Col span={12} style={{ textAlign: 'center' }}>
                <Statistic
                  title={t("Dashboard.Avg")}
                  value={formatValue(avgValue)}
                  precision={2}
                  valueStyle={{ color: "#4085c6", fontSize: 42 }}
                  prefix={<CalculatorOutlined />}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      );
    }

    return null;
  };

  const [refresh, setRefresh] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({});

  const isComparisonMode = () => {
    return comparisonFilterEnabled &&
      filterAnswersRequest?.dateRange2 &&
      filterAnswersRequest?.dateRange2[0] &&
      filterAnswersRequest?.dateRange2[1];
  };

  // FunÃ§Ã£o helper para formatar perÃ­odo de data (usa utilitÃ¡rio com fallback local)
  const isComparisonVisualMode = useCallback(() => {
    return comparisonVisualMode;
  }, [comparisonVisualMode]);

  const formatDateRange = useCallback((dateRange) => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return '';
    const formatted = formatDateRangeUtil(dateRange, currentLanguage);
    // Fallback para formataÃ§Ã£o local se utilitÃ¡rio nÃ£o funcionar
    if (!formatted) {
      const start = dayjs(dateRange[0]).format('DD/MM/YYYY');
      const end = dayjs(dateRange[1]).format('DD/MM/YYYY');
      return `${start} - ${end}`;
    }
    return formatted;
  }, [currentLanguage]);

  // Componente helper para renderizar perÃ­odo com bolinha colorida (memoizado)
  const renderPeriodWithDot = useCallback((period, dateRange) => {
    const periodLabel = t("Dashboard.Period");
    const dateText = formatDateRange(dateRange);

    return (
      <span style={periodWithDotContainerStyle}>
        <PeriodDot period={period} />
        <span>{periodLabel} {period}: {dateText}</span>
      </span>
    );
  }, [t, formatDateRange]);

  // Intercala tarefas dos dois perÃ­odos quando em modo de comparaÃ§Ã£o (otimizado)
  const interleavedTasks = useMemo(() => {
    // Verifica modo de comparaÃ§Ã£o baseado diretamente nos dados, nÃ£o em filterAnswersRequest
    const hasPeriod2Data = isValidArray(taskIdsList2);
    const isComparison = isComparisonVisualMode();

    if (!isComparison || !hasPeriod2Data) {
      // Se nÃ£o estiver em modo de comparaÃ§Ã£o, retorna apenas perÃ­odo 1
      return (taskIdsList || []).map(task => ({ ...task, period: 1 }));
    }

    const tasks1 = (taskIdsList || []).map(task => ({ ...task, period: 1 }));
    const tasks2 = (taskIdsList2 || []).map(task => ({ ...task, period: 2 }));

    //concat both lists and sort the result by enddate descending
    return tasks1.concat(tasks2).sort((a, b) => (dayjs(b.endDate).valueOf() || 0) - (dayjs(a.endDate).valueOf() || 0));

  }, [taskIdsList, taskIdsList2, filterAnswersRequest?.dateRange2]);

  // FunÃ§Ã£o helper para processar dados de um perÃ­odo especÃ­fico
  const processFieldData = (x2, answersData) => {
    if (!answersData) return { answers: [], categories: [], data: [], geoValues: [] };

    const x2question = questions.find(q => q.id === x2.id);

    // Monta o array completo de IDs (versÃ£o atual + todas as versÃµes ligadas), em lowercase
    let ids = [];
    if (x2question?.baseId) {
      const baseQuestions = questions.filter(
        q => q.baseId === x2question.baseId || q.id === x2question.baseId
      );
      baseQuestions.forEach(q => ids.push(q.id.toLowerCase()));
    } else {
      ids.push(x2.id.toLowerCase());
    }

    // Filtra tudo de uma vez (questionId OU originBaseId), usando lowercase
    let answers = (answersData || []).filter(ans =>
      (ans.questionId && ids.includes(ans.questionId.toLowerCase())) ||
      (ans.originBaseId && ids.includes(ans.originBaseId.toLowerCase()))
    );

    // Se houver mix de com e sem originBaseId, mantÃ©m sÃ³ os sem
    const hasWithBase = answers.some(a => a.originBaseId);
    const hasWithoutBase = answers.some(a => !a.originBaseId);
    if (hasWithBase && hasWithoutBase) {
      answers = answers.filter(a => !a.originBaseId);
    }

    // Para geolocation
    if (x2.questionTypeCode === 'geolocation') {
      const geoValues = answers.map(a => a.answer);
      return { answers, categories: [], data: [], geoValues };
    }

    // Monta counts, categories e data
    const answerCounts = answers.reduce((acc, curr) => {
      const key = curr.answer || 'N/A';
      acc[key] = (acc[key] || 0) + curr.answerCount;
      return acc;
    }, {});
    const categories = Object.keys(answerCounts);
    const data = Object.values(answerCounts);

    return { answers, categories, data, geoValues: [] };
  };

  const handleFilterSelect = (questionId, valor) => {
    setSelectedFilters((prevFilters) => {
      const currentValues = prevFilters[questionId] || [];
      const newValues = currentValues.includes(valor)
        ? currentValues.filter((item) => item !== valor)
        : [...currentValues, valor];
      const updatedFilters = { ...prevFilters };
      if (newValues.length > 0) {
        updatedFilters[questionId] = newValues;
      } else {
        delete updatedFilters[questionId];
      }
      selectedFiltersRef.current = updatedFilters;
      return updatedFilters;
    });

    // Aplica filtro imediatamente ao clicar em opÃ§Ã£o do grÃ¡fico.
    fetchAnswers();
  };

  useEffect(() => {
    if (refresh) {
      setRefresh(false);
    }
  }, [refresh]);

  // Limpar dados do perÃ­odo 2 quando dateRange2 for limpo
  useEffect(() => {
    if (!filterAnswersRequest?.dateRange2 || !filterAnswersRequest?.dateRange2[0] || !filterAnswersRequest?.dateRange2[1]) {
      setFormAnswers2(null);
      setTaskStatusCounts2(null);
      setTasksByLocation2([]);
      setTaskIdsList2([]);
    }
  }, [filterAnswersRequest?.dateRange2]);

  const fetchAnswers = async (overrides = {}) => {
    if (!form) return;
    setLoading(true);

    const requestState = filterAnswersRequestRef.current ?? filterAnswersRequest;
    let filtersStateForParams = selectedFiltersRef.current ?? selectedFilters;
    if (typeof filtersStateForParams !== "object" || Array.isArray(filtersStateForParams)) {
      filtersStateForParams = {};
    }

    if (form != lastLoadedForm || formFilho != lastLoadedFormFilho) {
      setFormDataMax(null);
      selectedFiltersRef.current = {};
      setSelectedFilters({});
      filtersStateForParams = {};
      await Api.post(`PowerBI/getFormData/${form}${formFilho ? "/" + formFilho?.id : ""}`)
        .then((res) => {
          const itemWithMaxVersion = res.data.response?.reduce(
            (maxItem, currentItem) =>
              currentItem.version > maxItem.version ? currentItem : maxItem,
            res.data.response[0]
          );
          const allQuestions = res.data.response.flatMap((form) =>
            form.sections?.flatMap((section) =>
              section.fields?.map((field) => ({
                id: field.id,
                label: field.label,
                type: field.questionTypeCode,
                chartType: field.chartType,
                visualTitle: field.visualTitle,
                visualSubTitle: field.visualSubTitle,
                agregationType: field.agregationType || null,
                version: field.version,
                baseId: field.previousVersionId,
                questionTypeCode: field.questionTypeCode,
                isAtr: field.isAtr
              })) || []
            ) || []
          );
          setQuestions(allQuestions);
          setFormDataMax(itemWithMaxVersion);
          setLastLoadedForm(form);
          setLastLoadedFormFilho(formFilho?.id);
        });
    }

    const buildParams = (usePeriod2 = false) => {
      const baseParams = {
        userId: requestState.userId,
        locationId: requestState.locationId,
        locationIncludeChildren: requestState.locationIncludeChildren,
        formId: form,
        versionId: formFilho?.id,
        filters: Object.entries(filtersStateForParams).map(([questionId, answers]) => {
          const findQuestion = formAnswers?.find(
            (answer) =>
              answer.questionId?.toLowerCase() === questionId.toLowerCase() ||
              answer.originBaseId?.toLowerCase() === questionId.toLowerCase()
          );
          return {
            QuestionId: findQuestion?.questionId ?? questionId,
            OriginBaseId: findQuestion?.originBaseId || undefined,
            Answers: (Array.isArray(answers) ? answers : []).filter((x) => x),
          };
        }),
      };

      if (usePeriod2) {
        // Fallback para P1 quando P2 nÃ£o estiver definido; normalizar para ISO como em P1
        const startDateRaw = requestState.startDate2 ?? requestState.startDate;
        const endDateRaw = requestState.endDate2 ?? requestState.endDate;
        const startDate = startDateRaw != null && typeof startDateRaw === "object" && typeof startDateRaw.toISOString === "function" ? startDateRaw.toISOString() : startDateRaw;
        const endDate = endDateRaw != null && typeof endDateRaw === "object" && typeof endDateRaw.toISOString === "function" ? endDateRaw.toISOString() : endDateRaw;
        return {
          ...baseParams,
          startDate,
          endDate,
        };
      } else {
        const startDate = overrides.startDate ?? requestState.startDate;
        const endDate = overrides.endDate ?? requestState.endDate;
        return {
          ...baseParams,
          startDate: startDate != null && typeof startDate === "object" && typeof startDate.toISOString === "function" ? startDate.toISOString() : startDate,
          endDate: endDate != null && typeof endDate === "object" && typeof endDate.toISOString === "function" ? endDate.toISOString() : endDate,
        };
      }
    };

    // Buscar dados do perÃ­odo 1
    const params1 = buildParams(false);

    // Se estiver em modo de comparaÃ§Ã£o, buscar dados de ambos os perÃ­odos em paralelo (allSettled para falha parcial)
    if (isComparisonMode()) {
      const params2 = buildParams(true);
      const [result1, result2] = await Promise.allSettled([
        Api.post(`PowerBI/getFormAnswers`, params1),
        Api.post(`PowerBI/getFormAnswers`, params2)
      ]);

      if (result1.status === "fulfilled" && (result1.value?.data?.response ?? result1.value?.data?.Response)) {
        const res = result1.value.data.response ?? result1.value.data.Response;
        setFormAnswers(res.answers ?? res.Answers ?? null);
        setTaskStatusCounts(res.TaskStatusCounts ?? res.taskStatusCounts ?? null);
        setTasksByLocation(res.filteredTasksByLocation ?? res.FilteredTasksByLocation ?? []);
        setTaskIdsList(res.tasks ?? res.Tasks ?? []);
      } else {
        if (result1.status === "rejected") {
          message.error("NÃ£o foi possÃ­vel carregar os dados do perÃ­odo 1.");
        }
        setFormAnswers(null);
        setTaskStatusCounts(null);
        setTasksByLocation([]);
        setTaskIdsList([]);
      }

      if (result2.status === "fulfilled" && (result2.value?.data?.response ?? result2.value?.data?.Response)) {
        const res = result2.value.data.response ?? result2.value.data.Response;
        setFormAnswers2(res.answers ?? res.Answers ?? null);
        setTaskStatusCounts2(res.TaskStatusCounts ?? res.taskStatusCounts ?? null);
        setTasksByLocation2(res.filteredTasksByLocation ?? res.FilteredTasksByLocation ?? []);
        setTaskIdsList2(res.tasks ?? res.Tasks ?? []);
      } else {
        if (result2.status === "rejected") {
          message.error("NÃ£o foi possÃ­vel carregar os dados do perÃ­odo 2.");
        }
        setFormAnswers2(null);
        setTaskStatusCounts2(null);
        setTasksByLocation2([]);
        setTaskIdsList2([]);
      }
    } else {
      // Buscar apenas dados do perÃ­odo 1
      var answers1 = await Api.post(`PowerBI/getFormAnswers`, params1);
      const res1 = answers1.data.response ?? answers1.data.Response;
      if (res1) {
        setFormAnswers(res1.answers ?? res1.Answers ?? null);
        setTaskStatusCounts(res1.TaskStatusCounts ?? res1.taskStatusCounts ?? null);
        setTasksByLocation(res1.filteredTasksByLocation ?? res1.FilteredTasksByLocation ?? []);
        setTaskIdsList(res1.tasks ?? res1.Tasks ?? []);
      } else {
        setFormAnswers(null);
        setTaskStatusCounts(null);
        setTasksByLocation([]);
        setTaskIdsList([]);
      }
      // Limpar dados do perÃ­odo 2 se nÃ£o estiver em modo de comparaÃ§Ã£o
      setFormAnswers2(null);
      setTaskStatusCounts2(null);
      setTasksByLocation2([]);
      setTaskIdsList2([]);
    }

    setLoading(false);
  };

  useEffect(() => {
    if (clear) {
      setClear(false);
    }
  }, [clear]);

  const clearFilters = () => {
    selectedFiltersRef.current = {};
    setSelectedFilters({});
    setFilterApplied(false);
    setClear(true);
  };

  const onClearFilter = async (target) => {
    if (target === "pai") {
      setForm(undefined);
    } else if (target === "filho") {
      setFormFilho(undefined);
    }
  };

  function crossFilter() {
    const comparisonNow = isComparisonMode();
    setComparisonApplied(true);
    setComparisonVisualMode(comparisonNow);
    fetchAnswers();
  }

  const formatDate = (data) => (data ? moment(data).format('YYYY-MMM-DD HH:mm') : "â€”");

  const cache = useRef(
    new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: 125
    })
  );

  const finalizedTotal = useMemo(() => {
    const doneFromStatus = taskStatusCounts?.Done ?? taskStatusCounts?.done;
    if (doneFromStatus != null) {
      return doneFromStatus;
    }
    return filteredTasksData?.done?.length ?? 0;
  }, [taskStatusCounts, filteredTasksData]);

  return (
    <Container>
      <Row align="middle" justify="center">
        <Col span={24}>
          <Card
            style={{ marginBottom: 24 }}
            bodyStyle={{ padding: 20 }}
          >
            <Row align="middle" justify="space-between" style={{ marginBottom: 16 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 500 }}>
                {t('Dashboard.LastUpdate')}: <span style={{ color: '#8c8c8c' }}>{lastExecution}</span>
              </Typography.Text>
              {(formId || form) && (
                <Typography.Text type="secondary" style={{ fontSize: 14, color: '#595959', fontWeight: 600 }}>
                  {t("Done")}: <span style={{ color: '#262626', fontSize: 18, fontWeight: 700, marginLeft: 4 }}>{finalizedTotal}</span>
                </Typography.Text>
              )}
            </Row>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 16 }}>
                  {!formId && (
                    <div style={filterFieldStyle}>
                      <Typography.Text strong style={{ fontSize: 12, color: '#595959', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {t("Dashboard.FilterByForm")}
                      </Typography.Text>
                      <SelectSearch
                        placeholder={t("BtnSearch")}
                        onChange={onChange}
                        optionsData={formsOptions}
                        onClear={() => onClearFilter("pai")}
                        style={{ width: "100%" }}
                        disabled={loading || formId}
                      />
                    </div>
                  )}
                  <div style={filterFieldStyle}>
                    <Typography.Text strong style={{ fontSize: 12, color: '#595959', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {t("Dashboard.FilterByVersionOptional")}
                    </Typography.Text>
                    <SelectSearch
                      placeholder={t("Version")}
                      onChange={onChangeFilho}
                      onClear={() => onClearFilter("filho")}
                      optionsData={formsFilhosOptions}
                      style={{ width: "100%" }}
                      disabled={loading}
                    />
                  </div>
                  <div style={{ minWidth: 200 }}>
                    <Typography.Text strong style={{ fontSize: 12, color: '#595959', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {t("Dashboard.FilterByDateRange")}
                    </Typography.Text>
                    <DatePicker.RangePicker
                      value={filterAnswersRequest?.dateRange}
                      onChange={handleDateRangeChange}
                      style={{ width: "100%" }}
                      disabled={loading}
                      locale={{
                        lang: {
                          locale: dayjsLocale,
                          monthFormat: 'MMM',
                          dayFormat: 'DD',
                          yearFormat: 'YYYY',
                          dateFormat: 'YYYY-MMM-DD',
                          dateTimeFormat: 'YYYY-MMM-DD HH:mm:ss',
                          monthBeforeYear: false,
                          yearSuffix: '',
                          monthSuffix: '',
                          daySuffix: '',
                          today: dayjsLocale === 'pt-br' ? 'Hoje' : 'Today',
                          now: dayjsLocale === 'pt-br' ? 'Agora' : 'Now',
                          backToToday: dayjsLocale === 'pt-br' ? 'Voltar para hoje' : 'Back to today',
                          ok: dayjsLocale === 'pt-br' ? 'OK' : 'OK',
                          timeSelect: dayjsLocale === 'pt-br' ? 'Selecionar hora' : 'Select time',
                          timeSelectLabel: dayjsLocale === 'pt-br' ? 'Selecionar hora' : 'Select time',
                          timeSelectOk: dayjsLocale === 'pt-br' ? 'OK' : 'OK',
                          timeSelectCancel: dayjsLocale === 'pt-br' ? 'Cancelar' : 'Cancel',
                          dateSelect: dayjsLocale === 'pt-br' ? 'Selecionar data' : 'Select date',
                          dateSelectLabel: dayjsLocale === 'pt-br' ? 'Selecionar data' : 'Select date',
                          dateSelectOk: dayjsLocale === 'pt-br' ? 'OK' : 'OK',
                          dateSelectCancel: dayjsLocale === 'pt-br' ? 'Cancelar' : 'Cancel',
                          monthSelect: dayjsLocale === 'pt-br' ? 'Selecionar mÃªs' : 'Select month',
                          monthSelectLabel: dayjsLocale === 'pt-br' ? 'Selecionar mÃªs' : 'Select month',
                          monthSelectOk: dayjsLocale === 'pt-br' ? 'OK' : 'OK',
                          monthSelectCancel: dayjsLocale === 'pt-br' ? 'Cancelar' : 'Cancel',
                          yearSelect: dayjsLocale === 'pt-br' ? 'Selecionar ano' : 'Select year',
                          yearSelectLabel: dayjsLocale === 'pt-br' ? 'Selecionar ano' : 'Select year',
                          yearSelectOk: dayjsLocale === 'pt-br' ? 'OK' : 'OK',
                          yearSelectCancel: dayjsLocale === 'pt-br' ? 'Cancelar' : 'Cancel',
                          decadeSelect: dayjsLocale === 'pt-br' ? 'Selecionar dÃ©cada' : 'Select decade',
                          decadeSelectLabel: dayjsLocale === 'pt-br' ? 'Selecionar dÃ©cada' : 'Select decade',
                          decadeSelectOk: dayjsLocale === 'pt-br' ? 'OK' : 'OK',
                          decadeSelectCancel: dayjsLocale === 'pt-br' ? 'Cancelar' : 'Cancel',
                          rangePlaceholder: [t('Reports.StartDate'), t('Reports.EndDate')]
                        }
                      }}
                    />
                  </div>
                  {comparisonFilterEnabled && (
                    <div style={filterFieldStyle}>
                      <Typography.Text strong style={{ fontSize: 12, color: '#595959', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {t("Dashboard.FilterByDateRange2")}
                      </Typography.Text>
                      <DatePicker.RangePicker
                        value={filterAnswersRequest?.dateRange2}
                        onChange={handleDateRangeChange2}
                        style={{ width: "100%" }}
                        disabled={loading}
                        locale={{
                          lang: {
                            locale: dayjsLocale,
                            monthFormat: 'MMM',
                            dayFormat: 'DD',
                            yearFormat: 'YYYY',
                            dateFormat: 'YYYY-MMM-DD',
                            dateTimeFormat: 'YYYY-MMM-DD HH:mm:ss',
                            monthBeforeYear: false,
                            yearSuffix: '',
                            monthSuffix: '',
                            daySuffix: '',
                            today: dayjsLocale === 'pt-br' ? 'Hoje' : 'Today',
                            now: dayjsLocale === 'pt-br' ? 'Agora' : 'Now',
                            backToToday: dayjsLocale === 'pt-br' ? 'Voltar para hoje' : 'Back to today',
                            ok: dayjsLocale === 'pt-br' ? 'OK' : 'OK',
                            timeSelect: dayjsLocale === 'pt-br' ? 'Selecionar hora' : 'Select time',
                            timeSelectLabel: dayjsLocale === 'pt-br' ? 'Selecionar hora' : 'Select time',
                            timeSelectOk: dayjsLocale === 'pt-br' ? 'OK' : 'OK',
                            timeSelectCancel: dayjsLocale === 'pt-br' ? 'Cancelar' : 'Cancel',
                            dateSelect: dayjsLocale === 'pt-br' ? 'Selecionar data' : 'Select date',
                            dateSelectLabel: dayjsLocale === 'pt-br' ? 'Selecionar data' : 'Select date',
                            dateSelectOk: dayjsLocale === 'pt-br' ? 'OK' : 'OK',
                            dateSelectCancel: dayjsLocale === 'pt-br' ? 'Cancelar' : 'Cancel',
                            monthSelect: dayjsLocale === 'pt-br' ? 'Selecionar mÃªs' : 'Select month',
                            monthSelectLabel: dayjsLocale === 'pt-br' ? 'Selecionar mÃªs' : 'Select month',
                            monthSelectOk: dayjsLocale === 'pt-br' ? 'OK' : 'OK',
                            monthSelectCancel: dayjsLocale === 'pt-br' ? 'Cancelar' : 'Cancel',
                            yearSelect: dayjsLocale === 'pt-br' ? 'Selecionar ano' : 'Select year',
                            yearSelectLabel: dayjsLocale === 'pt-br' ? 'Selecionar ano' : 'Select year',
                            yearSelectOk: dayjsLocale === 'pt-br' ? 'OK' : 'OK',
                            yearSelectCancel: dayjsLocale === 'pt-br' ? 'Cancelar' : 'Cancel',
                            decadeSelect: dayjsLocale === 'pt-br' ? 'Selecionar dÃ©cada' : 'Select decade',
                            decadeSelectLabel: dayjsLocale === 'pt-br' ? 'Selecionar dÃ©cada' : 'Select decade',
                            decadeSelectOk: dayjsLocale === 'pt-br' ? 'OK' : 'OK',
                            decadeSelectCancel: dayjsLocale === 'pt-br' ? 'Cancelar' : 'Cancel',
                            rangePlaceholder: [t('Reports.StartDate'), t('Reports.EndDate')]
                          }
                        }}
                      />
                    </div>
                  )}
                  {!userId && !user?.showOnlyMyTasks && (
                    <div style={filterFieldStyle}>
                      <Typography.Text strong style={{ fontSize: 12, color: '#595959', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {t("Dashboard.ResponsibleUserFilter")}
                      </Typography.Text>
                      {renderAnswersUserDropdown()}
                    </div>
                  )}
                  <div style={filterFieldStyle}>
                    <Typography.Text strong style={{ fontSize: 12, color: '#595959', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {t("Dashboard.FilterByLocation")}
                    </Typography.Text>
                    {renderAnswersLocationDropdown()}
                  </div>
                  <div style={{ minWidth: 140 }}>
                    <Typography.Text strong style={{ fontSize: 12, color: '#595959', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {t("Dashboard.HowManyColumns")}
                    </Typography.Text>
                    <Select
                      defaultValue={3}
                      value={cols}
                      onChange={(e) => setCols(e)}
                      style={{ width: "100%" }}
                    >
                      <Select.Option value={1}>{t("Dashboard.OneColumn")}</Select.Option>
                      <Select.Option value={2}>{t("Dashboard.TwoColumns")}</Select.Option>
                      <Select.Option value={3}>{t("Dashboard.ThreeColumns")}</Select.Option>
                      <Select.Option value={4}>{t("Dashboard.FourColumns")}</Select.Option>
                    </Select>
                  </div>
                  <div style={filterFieldStyle}>
                    <Typography.Text strong style={{ fontSize: 12, color: '#595959', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {t("Dashboard.ComparisonFilterEnabled", "Habilitar comparaÃ§Ã£o")}
                    </Typography.Text>
                    <Switch checked={comparisonFilterEnabled} onChange={handleComparisonToggle} />
                  </div>
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                    <Button
                      type="default"
                      icon={<MinusCircleOutlined />}
                      disabled={Object.keys(selectedFilters).length === 0 || loading}
                      onClick={clearFilters}
                    >
                      {t("Dashboard.ClearCrossFilter")}
                    </Button>
                    <Button
                      type="primary"
                      icon={<SearchOutlined />}
                      disabled={loading}
                      onClick={() => crossFilter()}
                    >
                      {t("Dashboard.Filter")}
                    </Button>
                  </div>
                </div>
                {filterAnswersRequest?.locationId != null && filterAnswersRequest?.locationId !== "" && (
                  <Row style={{ marginTop: 12 }}>
                    <Col span={24}>{renderAnswersLocationBelowDropdown()}</Col>
                  </Row>
                )}
              </Col>
            </Row>
          </Card>

          {isComparisonVisualMode() ? (
            <>
              {/* Modo de ComparaÃ§Ã£o - TasksByLocation (GrÃ¡fico Ãšnico com Dados Combinados) */}
              <Row gutter={[16, 16]} style={{ marginTop: 32, marginBottom: 32 }} >
                <Col span={24}>
                  <Card
                    title={
                      <Row justify="space-between" align="center">
                        <Col>
                          <Typography.Text strong style={{ fontSize: 16, color: '#262626', fontWeight: 600 }}>
                            {t("Dashboard.TasksByLocation")}
                          </Typography.Text>
                        </Col>
                        {filterAnswersRequest.locationId && (
                          <Col>
                            <Button
                              type="primary"
                              icon={<MinusOutlined />}
                              disabled={loading}
                              onClick={() => handleAnswersSearchLocation(null, "combo")}
                              size="small"
                            >
                              {t("Dashboard.ClearLocation")}
                            </Button>
                          </Col>
                        )}
                      </Row>
                    }
                    style={{ marginBottom: 24, height: '100%' }}
                    bodyStyle={{ padding: 16, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 300 }}
                  >
                    {loading ? (
                      <Row justify="center" align="middle" style={{ padding: 60 }}><Spin size="large" /></Row>
                    ) : (
                      <>
                        <LocationTreeComparison
                          tasksByLocation={tasksByLocation}
                          tasksByLocation2={tasksByLocation2}
                          handleAnswersSearchLocation={handleAnswersSearchLocation}
                          renderPeriodWithDot={renderPeriodWithDot}
                          filterAnswersRequest={filterAnswersRequest}
                        />
                      </>
                    )}
                  </Card>
                </Col>
              </Row>
              {/* Modo de ComparaÃ§Ã£o - AnswersByDate (Um GrÃ¡fico por PerÃ­odo) */}
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Card
                    title={
                      <Typography.Text strong style={{ fontSize: 16, color: '#262626', fontWeight: 600 }}>
                        {t("Dashboard.AnswersByDate")} - {renderPeriodWithDot(1, filterAnswersRequest?.dateRange)}
                      </Typography.Text>
                    }
                    style={{ marginBottom: 24, height: '100%' }}
                    bodyStyle={{ padding: 16, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 300 }}
                  >
                    {loading ? (
                      <Row justify="center" align="middle" style={{ padding: 60 }}>
                        <Spin size="large" />
                      </Row>
                    ) : (
                      <AnswersByDateChart
                        taskIdsList={taskIdsList}
                        dateRange={filterAnswersRequest?.dateRange}
                      />
                    )}
                  </Card>
                </Col>
                <Col span={12}>
                  <Card
                    title={
                      <Typography.Text strong style={{ fontSize: 16, color: '#262626', fontWeight: 600 }}>
                        {t("Dashboard.AnswersByDate")} - {renderPeriodWithDot(2, filterAnswersRequest?.dateRange2)}
                      </Typography.Text>
                    }
                    style={{ marginBottom: 24, height: '100%' }}
                    bodyStyle={{ padding: 16, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 300 }}
                  >
                    {loading ? (
                      <Row justify="center" align="middle" style={{ padding: 60 }}>
                        <Spin size="large" />
                      </Row>
                    ) : (
                      <AnswersByDateChart
                        taskIdsList={taskIdsList2}
                        dateRange={filterAnswersRequest?.dateRange2}
                      />
                    )}
                  </Card>
                </Col>
              </Row>
            </>
          ) : (
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Card
                  title={
                    <Row justify="space-between" align="center">
                      <Col>
                        <Typography.Text strong style={{ fontSize: 16, color: '#262626', fontWeight: 600 }}>
                          {t("Dashboard.TasksByLocation")}
                        </Typography.Text>
                      </Col>
                      {filterAnswersRequest.locationId && (
                        <Col>
                          <Button
                            type="primary"
                            icon={<MinusOutlined />}
                            disabled={loading}
                            onClick={() => handleAnswersSearchLocation(null, "combo")}
                            size="small"
                          >
                            {t("Dashboard.ClearLocation")}
                          </Button>
                        </Col>
                      )}
                    </Row>
                  }
                  style={{ marginBottom: 24, height: '100%' }}
                  bodyStyle={{ padding: 16, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 300 }}
                >
                  {loading ? (
                    <Row justify="center" align="middle" style={{ padding: 60 }}>
                      <Spin size="large" />
                    </Row>
                  ) : (
                    <LocationTree
                      tasksByLocation={tasksByLocation}
                      handleAnswersSearchLocation={handleAnswersSearchLocation}
                    />
                  )}
                </Card>
              </Col>
              <Col span={12}>
                <Card
                  title={
                    <Typography.Text strong style={{ fontSize: 16, color: '#262626', fontWeight: 600 }}>
                      {t("Dashboard.AnswersByDate")}
                    </Typography.Text>
                  }
                  style={{ marginBottom: 24, height: '100%' }}
                  bodyStyle={{ padding: 16, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 300 }}
                >
                  {loading ? (
                    <Row justify="center" align="middle" style={{ padding: 60 }}>
                      <Spin size="large" />
                    </Row>
                  ) : (
                    <AnswersByDateChart
                      taskIdsList={taskIdsList}
                      dateRange={filterAnswersRequest?.dateRange}
                    />
                  )}
                </Card>
              </Col>
            </Row>
          )}

          <Row gutter={[12, 12]}>
            <Col span={24}>
              {formDataMax?.sections
                .filter(section => section.fields.length > 0)
                .map((section, index) => (
                  <Fragment key={section.id}>
                    <Row style={{ marginBottom: 24, marginTop: index === 0 ? 24 : 0 }}>
                      <Col span={24} style={{ marginBottom: 24, paddingBottom: 16, borderBottom: '3px solid #4085c6', position: 'relative' }}>
                        <Typography.Title level={2} style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#262626', letterSpacing: '-0.5px' }}>
                          {t("Comp.Section")}: {section.section}
                        </Typography.Title>
                      </Col>
                    </Row>
                    {loading && (
                      <Row justify="center" style={{ padding: '60px 0' }}>
                        <Col><Spin size="large" /></Col>
                      </Row>
                    )}
                    {isComparisonVisualMode() ? (
                      <Row gutter={[12, 12]}>
                        {/* PerÃ­odo 1 */}
                        {(() => {
                          // Verifica se hÃ¡ algum grÃ¡fico pie/donut ou barChart de comparaÃ§Ã£o na seÃ§Ã£o
                          const hasPieOrDonut = section.fields.some(x2 => {
                            const fieldData = processFieldData(x2, formAnswers);
                            const fieldData2 = processFieldData(x2, formAnswers2);
                            const isPieOrDonut = (x2.chartType === 'pieChart' || x2.chartType === 'donutChart');
                            return isPieOrDonut && ((fieldData.data && fieldData.data.length > 0) || (fieldData2 && fieldData2.data && fieldData2.data.length > 0));
                          });
                          const hasBarChartComparison = section.fields.some(x2 => {
                            const fieldData = processFieldData(x2, formAnswers);
                            const fieldData2 = processFieldData(x2, formAnswers2);
                            const isBarChart = (x2.chartType === 'barChart');
                            return isBarChart && fieldData2 && fieldData2.data && fieldData2.data.length > 0;
                          });
                          const colSpan = (hasPieOrDonut || hasBarChartComparison) ? 24 : 12; // 24 colunas = 100% de largura quando hÃ¡ pie/donut ou barChart de comparaÃ§Ã£o
                          return (
                            <Col span={colSpan}>
                              <Row gutter={[12, 12]} style={{ display: 'flex', flexWrap: 'wrap' }}>
                                {section.fields.map(x2 => {
                                  const fieldData = processFieldData(x2, formAnswers);
                                  const fieldData2 = processFieldData(x2, formAnswers2);
                                  const isPieOrDonut = (x2.chartType === 'pieChart' || x2.chartType === 'donutChart');

                                  if (fieldData.answers.length === 0 && !x2.chartType && !x2.agregationType) {
                                    return null;
                                  }

                                  if (x2.questionTypeCode === 'geolocation') {
                                    return (
                                      <Col span={24} key={x2.id + '-map-period1'}>
                                        <Card
                                          style={{ height: '100%', marginBottom: 0 }}
                                          bodyStyle={{ padding: 16, minHeight: 300, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                                          title={<span style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>{x2.visualTitle ?? x2.label}</span>}
                                        >
                                          {loading ? (
                                            <Row justify="center" align="middle" style={{ padding: '60px 0' }}><Spin size="large" /></Row>
                                          ) : (
                                            <>
                                              {renderGeolocationMap(fieldData.geoValues, t)}
                                              {x2.visualSubTitle && (
                                                <Typography.Text type="secondary" style={{ marginTop: 16, fontSize: 13, fontStyle: 'italic', display: 'block' }}>
                                                  {x2.visualSubTitle}
                                                </Typography.Text>
                                              )}
                                            </>
                                          )}
                                        </Card>
                                      </Col>
                                    );
                                  }

                                  if (!fieldData.data || fieldData.data.length == 0 || loading) {
                                    // Se for pie/donut ou barChart de comparaÃ§Ã£o e nÃ£o tiver dados no perÃ­odo 1, ainda pode ter no perÃ­odo 2
                                    const isBarChartComparison = (x2.chartType === 'barChart') && fieldData2 && fieldData2.data && fieldData2.data.length > 0;
                                    if ((isPieOrDonut || isBarChartComparison) && fieldData2 && fieldData2.data && fieldData2.data.length > 0) {
                                      // Continua para renderizar grÃ¡fico combinado
                                    } else {
                                      return <></>;
                                    }
                                  }

                                  return (
                                    <Fragment key={x2.id + '-period1'}>
                                      {x2.agregationType && x2.questionTypeCode !== 'originInformation' && (
                                        <Col span={24} style={{ display: 'flex', flexDirection: 'column' }}>
                                          <Card
                                            style={{ flex: 1, height: '100%', marginBottom: 0, display: 'flex', flexDirection: 'column' }}
                                            bodyStyle={{ padding: 20, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}
                                            title={<span style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>{x2.visualTitle ?? x2.label}</span>}
                                          >
                                            {loading ? (
                                              <Row justify="center" align="middle" style={{ padding: '60px 0' }}><Spin size="large" /></Row>
                                            ) : (
                                              <>
                                                {renderAgr(x2, fieldData.data, fieldData.categories)}
                                                {x2.visualSubTitle && (
                                                  <div style={{ marginTop: 16, fontSize: 13, color: '#595959', textAlign: 'center', fontStyle: 'italic' }}>
                                                    {x2.visualSubTitle}
                                                  </div>
                                                )}
                                              </>
                                            )}
                                          </Card>
                                        </Col>
                                      )}
                                      {x2.chartType && (x2.questionTypeCode === 'originInformation' || !x2.agregationType) && (
                                        (() => {
                                          const isBarChartComparison = (x2.chartType === 'barChart') && fieldData2 && fieldData2.data && fieldData2.data.length > 0;

                                          if (isPieOrDonut) {
                                            return (
                                              <>
                                                {/* Card com os grÃ¡ficos - 50% */}
                                                <Col span={12} style={{ display: 'flex', flexDirection: 'column' }}>
                                                  <Card
                                                    style={{
                                                      height: '100%',
                                                      marginBottom: 0,
                                                      display: 'flex',
                                                      flexDirection: 'column'
                                                    }}
                                                    bodyStyle={{
                                                      padding: 16,
                                                      flex: 1,
                                                      minHeight: 300,
                                                      display: 'flex',
                                                      flexDirection: 'column',
                                                      justifyContent: 'center'
                                                    }}
                                                    title={
                                                      <div style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'flex-start',
                                                        width: '100%',
                                                        gap: '8px',
                                                        minWidth: 0
                                                      }}>
                                                        <span style={{
                                                          fontSize: 15,
                                                          fontWeight: 600,
                                                          color: '#262626',
                                                          flex: 1,
                                                          minWidth: 0,
                                                          wordBreak: 'break-word',
                                                          overflowWrap: 'break-word',
                                                          lineHeight: '1.4',
                                                          whiteSpace: 'normal'
                                                        }}>{x2.visualTitle ?? x2.label}</span>
                                                      </div>
                                                    }
                                                  >
                                                    {loading ? (
                                                      <Row justify="center" align="middle" style={{ padding: '60px 0' }}><Spin size="large" /></Row>
                                                    ) : (
                                                      <>
                                                        <Typography.Text type="secondary" style={{ fontSize: 12, marginBottom: 8, alignItems: 'center', display: 'flex', justifyContent: 'center' }}>
                                                          <Row justify="space-between" style={{ width: '75%' }}>
                                                            <Col>
                                                              {renderPeriodWithDot(1, filterAnswersRequest?.dateRange)}
                                                            </Col>
                                                            <Col>
                                                              {renderPeriodWithDot(2, filterAnswersRequest?.dateRange2)}
                                                            </Col>
                                                          </Row>
                                                        </Typography.Text>
                                                        {renderChart(x2, fieldData.data, fieldData.categories, null, fieldData2?.data, fieldData2?.categories, 'charts')}
                                                        {x2.visualSubTitle && (
                                                          <Typography.Text type="secondary" style={{ marginTop: 16, fontSize: 13, fontStyle: 'italic', display: 'block' }}>
                                                            {x2.visualSubTitle}
                                                          </Typography.Text>
                                                        )}
                                                      </>
                                                    )}
                                                  </Card>
                                                </Col>
                                                {/* Card com os detalhes - 50% */}
                                                <Col span={12} style={{ display: 'flex', flexDirection: 'column' }}>
                                                  <Card
                                                    style={{
                                                      height: '100%',
                                                      marginBottom: 0,
                                                      display: 'flex',
                                                      flexDirection: 'column'
                                                    }}
                                                    bodyStyle={{
                                                      padding: 16,
                                                      flex: 1,
                                                      minHeight: 300,
                                                      display: 'flex',
                                                      flexDirection: 'column'
                                                    }}
                                                    title={
                                                      <span style={{
                                                        fontSize: 15,
                                                        fontWeight: 600,
                                                        color: '#262626'
                                                      }}>{t('Dashboard.Details')}</span>
                                                    }
                                                  >
                                                    {loading ? (
                                                      <Row justify="center" align="middle" style={{ padding: '60px 0' }}><Spin size="large" /></Row>
                                                    ) : (
                                                      <>
                                                        {renderChart(x2, fieldData.data, fieldData.categories, null, fieldData2?.data, fieldData2?.categories, 'details')}
                                                      </>
                                                    )}
                                                  </Card>
                                                </Col>
                                              </>
                                            );
                                          } else if (isBarChartComparison) {
                                            // BarChart de comparaÃ§Ã£o ocupa row completa (span={24})
                                            return (
                                              <Col span={24} style={{ display: 'flex', flexDirection: 'column' }}>
                                                <Card
                                                  style={{
                                                    height: '100%',
                                                    marginBottom: 0,
                                                    display: 'flex',
                                                    flexDirection: 'column'
                                                  }}
                                                  bodyStyle={{
                                                    padding: 16,
                                                    flex: 1,
                                                    minHeight: 300,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'center'
                                                  }}
                                                  title={
                                                    <div style={{
                                                      display: 'flex',
                                                      justifyContent: 'space-between',
                                                      alignItems: 'flex-start',
                                                      width: '100%',
                                                      gap: '8px',
                                                      minWidth: 0
                                                    }}>
                                                      <span style={{
                                                        fontSize: 15,
                                                        fontWeight: 600,
                                                        color: '#262626',
                                                        flex: 1,
                                                        minWidth: 0,
                                                        wordBreak: 'break-word',
                                                        overflowWrap: 'break-word',
                                                        lineHeight: '1.4',
                                                        whiteSpace: 'normal'
                                                      }}>{x2.visualTitle ?? x2.label}</span>
                                                    </div>
                                                  }
                                                >
                                                  {loading ? (
                                                    <Row justify="center" align="middle" style={{ padding: '60px 0' }}><Spin size="large" /></Row>
                                                  ) : (
                                                    <>
                                                      {renderChart(x2, fieldData.data, fieldData.categories, null, fieldData2?.data, fieldData2?.categories, 'both')}
                                                      {x2.visualSubTitle && (
                                                        <Typography.Text type="secondary" style={{ marginTop: 16, fontSize: 13, fontStyle: 'italic', display: 'block' }}>
                                                          {x2.visualSubTitle}
                                                        </Typography.Text>
                                                      )}
                                                    </>
                                                  )}
                                                </Card>
                                              </Col>
                                            );
                                          } else {
                                            // GrÃ¡fico normal (nÃ£o Ã© comparaÃ§Ã£o)
                                            return (
                                              <Col span={12} style={{ display: 'flex', flexDirection: 'column' }}>
                                                <Card
                                                  style={{
                                                    height: '100%',
                                                    marginBottom: 0,
                                                    display: 'flex',
                                                    flexDirection: 'column'
                                                  }}
                                                  bodyStyle={{
                                                    padding: 16,
                                                    flex: 1,
                                                    minHeight: 300,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'center'
                                                  }}
                                                  title={
                                                    <div style={{
                                                      display: 'flex',
                                                      justifyContent: 'space-between',
                                                      alignItems: 'flex-start',
                                                      width: '100%',
                                                      gap: '8px',
                                                      minWidth: 0
                                                    }}>
                                                      <span style={{
                                                        fontSize: 15,
                                                        fontWeight: 600,
                                                        color: '#262626',
                                                        flex: 1,
                                                        minWidth: 0,
                                                        wordBreak: 'break-word',
                                                        overflowWrap: 'break-word',
                                                        lineHeight: '1.4',
                                                        whiteSpace: 'normal'
                                                      }}>{x2.visualTitle ?? x2.label}</span>
                                                    </div>
                                                  }
                                                >
                                                  {loading ? (
                                                    <Row justify="center" align="middle" style={{ padding: '60px 0' }}><Spin size="large" /></Row>
                                                  ) : (
                                                    <>
                                                      {renderChart(x2, fieldData.data, fieldData.categories, null)}
                                                      {x2.visualSubTitle && (
                                                        <Typography.Text type="secondary" style={{ marginTop: 16, fontSize: 13, fontStyle: 'italic', display: 'block' }}>
                                                          {x2.visualSubTitle}
                                                        </Typography.Text>
                                                      )}
                                                    </>
                                                  )}
                                                </Card>
                                              </Col>
                                            );
                                          }
                                        })()
                                      )}
                                    </Fragment>
                                  );
                                })}
                              </Row>
                            </Col>
                          );
                        })()}
                        {/* PerÃ­odo 2 - Apenas para grÃ¡ficos que nÃ£o sÃ£o pie/donut e nÃ£o sÃ£o barChart de comparaÃ§Ã£o */}
                        {section.fields.some(x2 => {
                          const fieldData = processFieldData(x2, formAnswers2);
                          const isPieOrDonut = (x2.chartType === 'pieChart' || x2.chartType === 'donutChart');
                          const isBarChartComparison = (x2.chartType === 'barChart') && fieldData.data && fieldData.data.length > 0;
                          return !isPieOrDonut && !isBarChartComparison && fieldData.data && fieldData.data.length > 0;
                        }) && (
                            <Col span={12}>
                              <Row gutter={[12, 12]} style={{ display: 'flex', flexWrap: 'wrap' }}>
                                {section.fields.map(x2 => {
                                  const fieldData = processFieldData(x2, formAnswers2);
                                  const isPieOrDonut = (x2.chartType === 'pieChart' || x2.chartType === 'donutChart');
                                  const isBarChartComparison = (x2.chartType === 'barChart') && fieldData.data && fieldData.data.length > 0;

                                  // Pula grÃ¡ficos pie/donut e barChart de comparaÃ§Ã£o no perÃ­odo 2 (jÃ¡ renderizados no perÃ­odo 1)
                                  if (isPieOrDonut || isBarChartComparison) return null;

                                  if (fieldData.answers.length === 0 && !x2.chartType && !x2.agregationType) {
                                    return null;
                                  }

                                  if (x2.questionTypeCode === 'geolocation') {
                                    return (
                                      <Col span={24} key={x2.id + '-map-period2'}>
                                        <Card
                                          style={{ height: '100%', marginBottom: 0 }}
                                          bodyStyle={{ padding: 16, minHeight: 300, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                                          title={<span style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>{x2.visualTitle ?? x2.label}</span>}
                                        >
                                          {loading ? (
                                            <Row justify="center" align="middle" style={{ padding: '60px 0' }}><Spin size="large" /></Row>
                                          ) : (
                                            <>
                                              {renderGeolocationMap(fieldData.geoValues, t)}
                                              {x2.visualSubTitle && (
                                                <Typography.Text type="secondary" style={{ marginTop: 16, fontSize: 13, fontStyle: 'italic', display: 'block' }}>
                                                  {x2.visualSubTitle}
                                                </Typography.Text>
                                              )}
                                            </>
                                          )}
                                        </Card>
                                      </Col>
                                    );
                                  }

                                  if (!fieldData.data || fieldData.data.length == 0 || loading) return <></>;

                                  return (
                                    <Fragment key={x2.id + '-period2'}>
                                      {x2.agregationType && x2.questionTypeCode !== 'originInformation' && (
                                        <Col span={24} style={{ display: 'flex', flexDirection: 'column' }}>
                                          <Card
                                            style={{ flex: 1, height: '100%', marginBottom: 0, display: 'flex', flexDirection: 'column' }}
                                            bodyStyle={{ padding: 20, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}
                                            title={<span style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>{x2.visualTitle ?? x2.label}</span>}
                                          >
                                            {loading ? (
                                              <Row justify="center" align="middle" style={{ padding: '60px 0' }}><Spin size="large" /></Row>
                                            ) : (
                                              <>
                                                {renderAgr(x2, fieldData.data, fieldData.categories)}
                                                {x2.visualSubTitle && (
                                                  <Typography.Text type="secondary" style={{ marginTop: 16, fontSize: 13, textAlign: 'center', fontStyle: 'italic', display: 'block' }}>
                                                    {x2.visualSubTitle}
                                                  </Typography.Text>
                                                )}
                                              </>
                                            )}
                                          </Card>
                                        </Col>
                                      )}
                                      {x2.chartType && (x2.questionTypeCode === 'originInformation' || !x2.agregationType) && (
                                        <Col span={24} style={{ display: 'flex', flexDirection: 'column' }}>
                                          <Card
                                            style={{
                                              height: '100%',
                                              marginBottom: 0,
                                              display: 'flex',
                                              flexDirection: 'column'
                                            }}
                                            bodyStyle={{
                                              padding: 16,
                                              flex: 1,
                                              minHeight: 300,
                                              display: 'flex',
                                              flexDirection: 'column',
                                              justifyContent: 'center'
                                            }}
                                            title={
                                              <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                                width: '100%',
                                                gap: '8px',
                                                minWidth: 0
                                              }}>
                                                <span style={{
                                                  fontSize: 15,
                                                  fontWeight: 600,
                                                  color: '#262626',
                                                  flex: 1,
                                                  minWidth: 0,
                                                  wordBreak: 'break-word',
                                                  overflowWrap: 'break-word',
                                                  lineHeight: '1.4',
                                                  whiteSpace: 'normal'
                                                }}>{x2.visualTitle ?? x2.label}</span>
                                              </div>
                                            }
                                          >
                                            {loading ? (
                                              <Row justify="center" align="middle" style={{ padding: '60px 0' }}><Spin size="large" /></Row>
                                            ) : (
                                              <>
                                                {renderChart(x2, fieldData.data, fieldData.categories, null)}
                                                {x2.visualSubTitle && (
                                                  <Typography.Text type="secondary" style={{ marginTop: 16, fontSize: 13, fontStyle: 'italic', display: 'block' }}>
                                                    {x2.visualSubTitle}
                                                  </Typography.Text>
                                                )}
                                              </>
                                            )}
                                          </Card>
                                        </Col>
                                      )}
                                    </Fragment>
                                  );
                                })}
                              </Row>
                            </Col>
                          )}
                      </Row>
                    ) : (
                      <Row gutter={[12, 12]} style={{ display: 'flex', flexWrap: 'wrap' }}>
                        {section.fields.map(x2 => {
                          const fieldData = processFieldData(x2, formAnswers);

                          if (fieldData.answers.length === 0 && !x2.chartType && !x2.agregationType) {
                            return null;
                          }

                          if (x2.questionTypeCode === 'geolocation') {
                            return (
                              <Col {...getResponsiveColProps(cols)} key={x2.id + '-map'}>
                                <Card
                                  style={{ height: '100%', marginBottom: 0 }}
                                  bodyStyle={{ padding: 16, minHeight: 300, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                                  title={<span style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>{x2.visualTitle ?? x2.label}</span>}
                                >
                                  {loading ? (
                                    <Row justify="center" align="middle" style={{ padding: '60px 0' }}><Spin size="large" /></Row>
                                  ) : (
                                    <>
                                      {renderGeolocationMap(fieldData.geoValues, t)}
                                      {x2.visualSubTitle && (
                                        <Typography.Text type="secondary" style={{ marginTop: 16, fontSize: 13, fontStyle: 'italic', display: 'block' }}>
                                          {x2.visualSubTitle}
                                        </Typography.Text>
                                      )}
                                    </>
                                  )}
                                </Card>
                              </Col>
                            );
                          }

                          if (!fieldData.data || fieldData.data.length == 0 || loading) return <></>;

                          return (
                            <Fragment key={x2.id}>
                              {x2.agregationType && x2.questionTypeCode !== 'originInformation' && (
                                <Col {...getResponsiveColProps(cols)} style={{ display: 'flex', flexDirection: 'column' }}>
                                  <Card
                                    style={{ flex: 1, height: '100%', marginBottom: 0, display: 'flex', flexDirection: 'column' }}
                                    bodyStyle={{ padding: 20, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}
                                    title={<span style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>{x2.visualTitle ?? x2.label}</span>}
                                  >
                                    {loading ? (
                                      <Row justify="center" align="middle" style={{ padding: '60px 0' }}><Spin size="large" /></Row>
                                    ) : (
                                      <>
                                        {renderAgr(x2, fieldData.data, fieldData.categories)}
                                        {x2.visualSubTitle && (
                                          <Typography.Text type="secondary" style={{ marginTop: 16, fontSize: 13, textAlign: 'center', fontStyle: 'italic', display: 'block' }}>
                                            {x2.visualSubTitle}
                                          </Typography.Text>
                                        )}
                                      </>
                                    )}
                                  </Card>
                                </Col>
                              )}
                              {x2.chartType && (x2.questionTypeCode === 'originInformation' || !x2.agregationType) && (
                                <ChartCardWrapper
                                  key={x2.id}
                                  cols={cols}
                                  getResponsiveColProps={getResponsiveColProps}
                                  title={x2.visualTitle ?? x2.label}
                                  loading={loading}
                                  visualSubTitle={x2.visualSubTitle}
                                  renderChart={(setHeaderButton) => renderChart(x2, fieldData.data, fieldData.categories, setHeaderButton)}
                                />
                              )}
                            </Fragment>
                          );
                        })}
                      </Row>
                    )}
                  </Fragment>
                ))}
            </Col>
          </Row>

          {interleavedTasks && interleavedTasks.length > 0 ?
            <Card
              style={{ marginTop: 32, marginBottom: 32 }}
              bodyStyle={{ padding: 0 }}
              title={<span style={{ fontSize: 16, fontWeight: 600, color: '#262626' }}>{t("Dashboard.Tasks")}</span>}
            >
              {loading ? <Row justify="center" align="middle" style={{ padding: '60px 0' }}><Spin size="large" /></Row> :
                <div style={{ display: 'flex', flexDirection: 'column', height: 500, overflow: 'hidden', borderRadius: 8 }}>
                  <Row
                    align="center"
                    style={{
                      padding: '14px 20px',
                      borderBottom: '2px solid #e8e8e8',
                      background: 'linear-gradient(to bottom, #fafafa 0%, #f5f5f5 100%)',
                      fontSize: 12,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      userSelect: 'none',
                      flexShrink: 0,
                      width: '100%',
                      boxSizing: 'border-box',
                      color: '#595959'
                    }}
                  >
                    {isComparisonVisualMode() && (
                      <Col style={{ flex: '0 0 30px', paddingRight: 12, textAlign: 'center' }} />
                    )}
                    <Col style={{ flex: '0 0 50px', paddingRight: 12, textAlign: 'center' }}><Typography.Text strong>{t('Dashboard.Link')}</Typography.Text></Col>
                    <Col style={{ flex: '0 0 110px', paddingRight: 12 }}><Typography.Text strong>{t('Code')}</Typography.Text></Col>
                    <Col style={{ flex: '1 1 200px', minWidth: 150, paddingRight: 12 }}><Typography.Text strong>{t('Form')}</Typography.Text></Col>
                    <Col style={{ flex: '1 1 250px', minWidth: 200, paddingRight: 12 }}><Typography.Text strong>{t('Description')}</Typography.Text></Col>
                    <Col style={{ flex: '0 0 70px', paddingRight: 12, textAlign: 'center' }}><Typography.Text strong>{t('Version')}</Typography.Text></Col>
                    <Col style={{ flex: '1 1 180px', minWidth: 150, paddingRight: 12 }}><Typography.Text strong>{t('Location')}</Typography.Text></Col>
                    <Col style={{ flex: '0 0 150px', paddingRight: 12 }}><Typography.Text strong>{t('FinishedOn')}</Typography.Text></Col>
                    <Col style={{ flex: '1 1 150px', minWidth: 120, paddingRight: 12 }}><Typography.Text strong>{t('AssignedTo')}</Typography.Text></Col>
                  </Row>

                  <div style={{ flex: 1, minHeight: 0 }}>
                    <AutoSizer>
                      {({ width, height }) => (
                        <RVList
                          width={width}
                          height={height}
                          rowCount={interleavedTasks.length}
                          deferredMeasurementCache={cache.current}
                          rowHeight={cache.current.rowHeight}
                          overscanRowCount={3}
                          rowClassName="virtualized-table-row"
                          rowRenderer={({ key, index, style, parent }) => {
                            const r = interleavedTasks[index];
                            const period = r?.period || 1;
                            return (
                              <CellMeasurer
                                cache={cache.current}
                                columnIndex={0}
                                key={key}
                                parent={parent}
                                rowIndex={index}
                              >
                                {({ registerChild }) => (
                                  <div
                                    ref={registerChild}
                                    style={{
                                      ...style,
                                      display: 'flex',
                                      alignItems: 'center',
                                      padding: '14px 20px',
                                      borderBottom: '1px solid #f0f0f0',
                                      fontSize: 14,
                                      background: index % 2 ? '#fafafa' : '#fff',
                                      width: '100%',
                                      boxSizing: 'border-box',
                                      transition: 'all 0.2s ease',
                                      cursor: 'default',
                                      opacity: 0,
                                      animation: `fadeInRow 0.3s ease forwards`,
                                      animationDelay: `${index * 0.02}s`
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = 'rgba(64, 133, 198, 0.08)';
                                      e.currentTarget.style.transform = 'translateX(2px)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = index % 2 ? '#fafafa' : '#fff';
                                      e.currentTarget.style.transform = 'translateX(0)';
                                    }}
                                  >
                                    {isComparisonVisualMode() && (
                                      <div style={{
                                        flex: '0 0 30px',
                                        paddingRight: 12,
                                        textAlign: 'center',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}>
                                        <PeriodDot
                                          period={period}
                                          size={12}
                                          showBorder={true}
                                          showShadow={true}
                                          style={{ border: '2px solid #fff' }}
                                        />
                                      </div>
                                    )}
                                    <div style={{ flex: '0 0 50px', paddingRight: 12, textAlign: 'center' }}>
                                      <a
                                        href={`/formbuilder/view-answer/${r.id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          color: '#4085c6',
                                          fontSize: 18,
                                          textDecoration: 'none',
                                          transition: 'color 0.2s ease',
                                          display: 'inline-block'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.color = '#32639e'}
                                        onMouseLeave={(e) => e.currentTarget.style.color = '#4085c6'}
                                      >
                                        <LinkOutlined />
                                      </a>
                                    </div>
                                    <div style={{
                                      flex: '0 0 110px',
                                      paddingRight: 12,
                                      fontFamily: 'monospace',
                                      fontSize: 13,
                                      color: '#595959',
                                      fontWeight: 500
                                    }}>
                                      {r.friendlyId ?? r.id}
                                    </div>
                                    <div style={{
                                      flex: '1 1 200px',
                                      minWidth: 150,
                                      paddingRight: 12,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      color: '#262626',
                                      fontWeight: 500
                                    }}>
                                      {r.formName}
                                    </div>
                                    <div style={{
                                      flex: '1 1 250px',
                                      minWidth: 200,
                                      paddingRight: 12,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      color: '#595959'
                                    }}>
                                      {r.description || r.name}
                                    </div>
                                    <div style={{
                                      flex: '0 0 70px',
                                      paddingRight: 12,
                                      textAlign: 'center',
                                      color: '#8c8c8c',
                                      fontSize: 13
                                    }}>
                                      {r.version + 1}
                                    </div>
                                    <div style={{
                                      flex: '1 1 180px',
                                      minWidth: 150,
                                      paddingRight: 12,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      color: '#595959'
                                    }}>
                                      {r.locationName}
                                    </div>
                                    <div style={{
                                      flex: '0 0 150px',
                                      paddingRight: 12,
                                      fontSize: 13,
                                      color: '#8c8c8c',
                                      fontFamily: 'monospace'
                                    }}>
                                      {formatDate(r.endDate)}
                                    </div>
                                    <div style={{
                                      flex: '1 1 150px',
                                      minWidth: 120,
                                      paddingRight: 12,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      color: '#262626'
                                    }}>
                                      {r.userName}
                                    </div>
                                  </div>
                                )}
                              </CellMeasurer>
                            );
                          }}
                        />
                      )}
                    </AutoSizer>
                  </div>
                </div>
              }
            </Card>
            : ""}
        </Col>
      </Row >
    </Container >
  );
};

export default NewPowerBI;












